import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Pressable,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
interface ConfirmModalProps {
  visible: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
}

const AlertIcon = ({ size = 48, color = tokens.colors.raw.red["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 8V12"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 16H12.01"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

export default function ConfirmModal({
  visible,
  title,
  message,
  confirmText,
  cancelText,
  type = 'danger',
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();

  // Use translated defaults if not provided
  const displayConfirmText = confirmText || t('common.confirm');
  const displayCancelText = cancelText || t('common.cancel');

  // Theme colors for dark mode
  const themeColors = {
    container: colors.card,
    title: colors.textPrimary,
    message: colors.textMuted,
  };

  const confirmVariant: 'primary' | 'destructive' = type === 'danger' ? 'destructive' : 'primary';

  const getTypeColor = () => {
    switch (type) {
      case 'danger':
        return COLORS.danger;
      case 'warning':
        return COLORS.warning;
      case 'info':
        return COLORS.secondary;
      default:
        return COLORS.danger;
    }
  };

  const typeColor = getTypeColor();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <Pressable style={styles.overlay} onPress={onCancel}>
        <Pressable style={[styles.container, { backgroundColor: themeColors.container }]} onPress={(e) => e.stopPropagation()}>
          {/* Icon */}
          <View style={[styles.iconContainer, { backgroundColor: `${typeColor}15` }]}>
            <AlertIcon size={48} color={typeColor} />
          </View>

          {/* Title */}
          <Text style={[styles.title, { color: themeColors.title }]}>{title}</Text>

          {/* Message */}
          <Text style={[styles.message, { color: themeColors.message }]}>{message}</Text>

          {/* Buttons */}
          <View style={styles.buttonContainer}>
            <Button variant="secondary" onPress={onCancel} style={{ flex: 1 }}>
              {displayCancelText}
            </Button>
            <Button variant={confirmVariant} onPress={onConfirm} style={{ flex: 1 }}>
              {displayConfirmText}
            </Button>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.absolute["5"],
  },
  container: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-16"],
    padding: tokens.spacing.absolute["6"],
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["4"],
  },
  title: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.medium,
    color: COLORS.neutral900,
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["2"],
  },
  message: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: COLORS.neutral600,
    textAlign: 'center',
    marginBottom: tokens.spacing.absolute["6"],
    lineHeight: 22,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
    width: '100%',
  },
});
